import { useState, useMemo, useEffect, useLayoutEffect } from 'react';
import {
    DrawerComp,
    ConfirmationDialog,
    InfoTooltip,
    RadioWithOptionalDatePicker,
    FundingDrawersDropdown
} from '../../components/molecules';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { ReactComponent as InfoErrorIcon } from '../../assets/icons/info-error.svg';
import { v4 as uuidv4 } from 'uuid';
import { Controller, useForm, useWatch } from 'react-hook-form';
import dayjs from 'dayjs';
import { BasicDatePicker, ComboSelect, TextboxInput, Typography, Checkbox, BasicButton } from '../../components/atoms';
import { Box, Grid, Chip, useMediaQuery, useTheme } from '@mui/material';
import { useAppSelector, useAppDispatch } from '../../common/hooks';
import {
    InvestmentSourcesType,
    GoalRecommendationType,
    CallRunpipePropsType,
    RetirementGoalsFormType
} from '../../common/types/clients-entity';
import { addAgeToDate, getYYYYMMDDfromDate, removeNullUndefined } from '../../common/helper';
import { getClientJourneyFlowType } from '../../utils';
import {
    formatCurrency,
    isEqual,
    checkFFEnabled,
    isMonthYearDatesEnabled,
    removeNonNumeric,
    getDurationColor
} from '../../utils';
import {
    CLIENT_PROFILE_STAGE4,
    DEFAULT_RETIREMENT_AGE,
    DEFAULT_CURRENCY_SYMBOL,
    EWS_DURATION_KEYS,
    FEATURE_NAMES,
    STATUSVARIANTS
} from '../../common/constants';
import investmentSource from '../../assets/icons/income_source.svg';
import { clearClientError, saveClient, updateClientInvestmentSources } from '../../features/client';
import { syncAdviceGoals } from '../../features/client-goals';
import { setAffectedEwsTerms } from '../../features/ews';
import { updateGlobalLoaderState } from '../../features/global/globalSlice';
import { Banner } from '../../components/molecules/Banner/Banner';
import { StickyRegion } from '../../containers/sticky-region';
import { InfoMessage } from '../../themes/styles/common';
interface AddInvestmentAccountsProps {
    // eslint-disable-next-line no-unused-vars
    onSubmit?: () => void;
    mode?: 'add' | 'edit';
    // eslint-disable-next-line no-unused-vars
    onEditSuccess?: (deletedAccounts?: string[], formFields?: any[]) => void;
    loading?: boolean;
    openDrawer: boolean;
    setOpenDrawer: any;
    goalDataRecomendation?: GoalRecommendationType;
    callRunpipe?: CallRunpipePropsType;
    // eslint-disable-next-line
    handleDeleteAccount?: (accountId: string) => void;
    selectedAccountId?: string;
    goalsWithNoFundingSource?: RetirementGoalsFormType[];
    needPrepopulationOnEdit?: boolean;
}

export const ItemContainer = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    gap: '8px',
    '& a': {
        color: theme.palette.background.paper
    }
}));

const Container = styled('div')(() => ({
    display: 'flex',
    alignItems: 'center'
}));

const FundingFormWrapper = styled('form')(() => ({
    height: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    fontSize: '14px'
}));

export const ErrorSpan = styled(Typography)(({ theme }) => ({
    fontSize: '13px',
    fontStyle: 'italic',
    fontWeight: 400,
    lineHeight: '12px',
    color: theme.palette.berry[400],
    margin: 0
}));

export const DeleteContainer = styled('div')(() => ({
    textAlign: 'center',
    marginTop: '24px',
    marginBottom: '30px',
    borderTop: '1px solid #E6E6E6',
    paddingTop: '8px'
}));

interface FrequencyProps {
    selected: boolean;
}

export const Frequency = styled('div')<FrequencyProps>(({ selected }) => ({
    width: '35px',
    height: '35px',
    borderRadius: '50%',
    aspectRatio: '1/1',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    border: '1px solid #E0E0E0',
    cursor: 'pointer',
    backgroundColor: selected ? '#E0E0E0' : 'transparent'
}));

export const Placeholder = styled('div')(() => ({
    height: '21px'
}));

const StyledRecommendationHelperText = styled(Typography)(({ theme }) => ({
    color: theme.palette.info[400],
    fontVariantNumeric: 'lining-nums tabular-nums',
    fontSize: theme.spacing(2),
    fontStyle: 'italic',
    fontWeight: 600,
    lineHeight: theme.spacing(2.5)
}));

export const AddInvestmentAccounts = ({
    openDrawer,
    setOpenDrawer,
    mode = 'add',
    onEditSuccess,
    callRunpipe,
    goalDataRecomendation,
    selectedAccountId = '', // add client feature flag
    goalsWithNoFundingSource,
    needPrepopulationOnEdit
}: AddInvestmentAccountsProps) => {
    const { t, i18n } = useTranslation();
    const language = i18n.language;
    const dispatch = useAppDispatch();
    const {
        orgInfo,
        goeConfig,
        globalConfig: { currencySymbol }
    } = useAppSelector((state) => state.global);
    const adviceClient = useAppSelector((state) => state.adviceClient);
    const adviceGoals = useAppSelector((state) => state.adviceGoals);
    const isPlanRun = adviceGoals?.global?.isPlanRun;
    const isPlanSavedOnce = adviceGoals?.global?.isPlanSavedOnce;

    const isFlow2 = getClientJourneyFlowType(adviceClient?.tenant) === 'flow2';
    const willCallUpdateClientProfile =
        !isPlanSavedOnce || (isFlow2 && !checkFFEnabled(FEATURE_NAMES['AEX-3590_3604_NEW_FLOW_UPDATE_SAVE_PLAN']));
    const getSourceKey = (source: any) => source?.accountId;
    const [fundingAccounts, setFundingAccounts] = useState<InvestmentSourcesType[]>(
        adviceClient.accounts.investmentsSources ?? []
    );
    const relationships = adviceClient.relationship;
    const { clientFirstName, clientLastName } = adviceClient;
    const [keyId, setKeyId] = useState(uuidv4());
    const [investmentSourceEdited, setInvestmentSourceEdited] = useState(false);
    const [needPrepopulation, setNeedPrepopulation] = useState(true);
    const [onEditPrepopulation, setOnEditPrepopulation] = useState(needPrepopulationOnEdit);
    const [selectedAccountForEdit, setSelectedAccountForEdit] = useState('');
    const [selectedAccountInDropdown, setSelectedAccountInDropdown] = useState('');
    const [deletedAccountIds, setDeletedAccountIds] = useState<string[]>([]);
    const [deleteModalState, setDeleteModalState] = useState(false);
    const [currentMode, setCurrentMode] = useState(mode);
    const [isDelete, setIsDelete] = useState(false);
    const [apiErrorToken, setApiErrorToken] = useState(0);

    const currentDate = dayjs();
    // AEX-3223: FF on treats the funding date at month granularity; FF off keeps the year-based comparison.
    const isFundingDateInFuture = (d: dayjs.Dayjs) =>
        isMonthYearDatesEnabled() ? d.isAfter(currentDate, 'month') : d.year() > currentDate.year();
    const [accountRecommendation, setAccountRecommendation] = useState<GoalRecommendationType | undefined>(
        goalDataRecomendation
    );
    const [showRecurringContributions, setShowRecurringContributions] = useState(false);
    const [showOneTimeFutureContribution, setShowOneTimeFutureContribution] = useState(false);
    const [recurringContributionsStartDateKey, setRecurringContributionsStartDateKey] = useState<string>('');
    const [recurringContributionsEndDateKey, setRecurringContributionsEndDateKey] = useState<string>('');

    const theme = useTheme();

    const isXl = useMediaQuery(theme.breakpoints.up('xl'));
    const isLg = useMediaQuery(theme.breakpoints.up('lg'));
    const isMd = useMediaQuery(theme.breakpoints.up('md'));
    const isSingleClientOnly = !adviceClient?.relationship || adviceClient?.relationship.length === 0;

    const getResponsiveWidth = () => {
        if (isXl) return '30%';
        if (isLg || isMd) return '40%';
        return '50%'; // default
    };

    const locationValue = orgInfo?.investmentAccountLocation?.['en']?.[0]?.value;
    const initialFormValues = {
        accountHolder: relationships.length === 0 ? 'primary' : '',
        location: locationValue ?? '',
        accountName: '',
        accountType: '',
        amount: undefined,
        fundingYear: null,
        recurringContributions: undefined,
        frequency: undefined,
        recurringContributionsEndDate: null,
        recurringContributionsEndDateKey: '',
        recurringContributionsStartDate: null,
        recurringContributionsStartDateKey: '',
        escalationContributions: 0,
        oneTimeFutureAmount: undefined
    };

    const { control, formState, handleSubmit, trigger, reset, getValues, setValue, setError, clearErrors, watch } =
        useForm<InvestmentSourcesType>({
            mode: 'all',
            reValidateMode: 'onSubmit',
            defaultValues: initialFormValues
        });
    const accountHolder = watch('accountHolder');
    const accountType = watch('accountType');
    const accountTypeDropdownValues = useAppSelector((state) => state.global.orgInfo?.investmentAccountType);
    const currentAccountTypeDropdownValues =
        accountTypeDropdownValues?.[i18n.language] || accountTypeDropdownValues?.['en'] || [];

    useEffect(() => {
        if (!needPrepopulation || !onEditPrepopulation) return;
        if (!accountHolder || !accountType) return;
        let holder = '';
        if (accountHolder === 'primary') {
            holder = clientFirstName;
        } else if (accountHolder === 'joint') {
            holder = t('TEXT_JOINT');
        } else {
            holder =
                relationships.find((r) => r.id === accountHolder)?.firstName ||
                adviceClient?.relationship?.[0]?.firstName ||
                '';
        }
        if (holder) {
            const currentAccountType =
                currentAccountTypeDropdownValues.find((item: Record<string, string>) => item.value === accountType)
                    ?.label || accountType;
            const autoName =
                accountHolder === 'joint' ? `${holder} ${currentAccountType}` : `${holder}'s ${currentAccountType}`;
            setValue('accountName', autoName, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
            clearErrors('accountName');
        }
    }, [accountHolder, accountType, clientFirstName, relationships, setValue, clearErrors, needPrepopulation]);

    const { errors, isValid } = formState;
    const recurringContributionsEndDateValue = useWatch({ control, name: 'recurringContributionsEndDate' });
    const recurringContributionsStartDateValue = useWatch({ control, name: 'recurringContributionsStartDate' });

    const handleReset = () => {
        setShowRecurringContributions(false); // Reset checkbox to unchecked
        setShowOneTimeFutureContribution(false); // Reset one-time future contribution checkbox
        setSelectedAccountInDropdown(''); // Reset dropdown selection
        setRecurringContributionsStartDateKey('');
        setRecurringContributionsEndDateKey('');
        reset({
            accountHolder: relationships.length === 0 ? 'primary' : '',
            location: locationValue ?? '',
            accountName: '',
            accountType: '',
            amount: undefined,
            fundingYear: null,
            recurringContributions: undefined,
            frequency: undefined,
            recurringContributionsEndDate: null,
            recurringContributionsEndDateKey: '',
            recurringContributionsStartDate: null,
            recurringContributionsStartDateKey: '',
            escalationContributions: 0,
            oneTimeFutureAmount: undefined
        });
    };
    const recurringContributions = watch('recurringContributions');
    const frequency = useWatch({ control, name: 'frequency' });
    const getIntegerValue = (value: any) => Number(String(value ?? '').replace(/\D/g, '')) || 0;

    const getAccountsSameDatesInfoMessage = () => {
        if (
            !showRecurringContributions ||
            !frequency ||
            !recurringContributionsStartDateValue ||
            !recurringContributionsEndDateValue
        ) {
            return null;
        }

        const startDate = dayjs(recurringContributionsStartDateValue, 'DD-MM-YYYY');
        const endDate = dayjs(recurringContributionsEndDateValue, 'DD-MM-YYYY');

        if (!startDate.isValid() || !endDate.isValid()) {
            return null;
        }

        if (isMonthYearDatesEnabled()) {
            if (startDate.year() === endDate.year() && startDate.month() === endDate.month()) {
                return t('ACCOUNT_DATES_SAME_MONTH_ERROR');
            }
        } else if (frequency === 'Annually') {
            if (startDate.year() === endDate.year()) {
                return t('ACCOUNT_DATES_SAME_YEAR_ERROR');
            }
        } else if (frequency === 'Monthly') {
            if (startDate.year() === endDate.year() && startDate.month() === endDate.month()) {
                return t('ACCOUNT_DATES_SAME_MONTH_ERROR');
            }
        }

        return null;
    };

    const infoMessage = useMemo(
        () => getAccountsSameDatesInfoMessage(),
        [
            showRecurringContributions,
            frequency,
            recurringContributionsStartDateValue,
            recurringContributionsEndDateValue,
            t
        ]
    );

    useEffect(() => {
        if (accountRecommendation) {
            // select the first Account selected for the goal with recommendation
            // if the goal doesn't have an Account then open drawer with empty fields
            // ref: AEX-1701
            const firstAccSelectedForTheGoalWithRecomendation =
                accountRecommendation?.originalData?.formFields?.sources?.investmentSource[0];
            setSelectedAccountForEdit(firstAccSelectedForTheGoalWithRecomendation?.accountNumber || '');
            const investmentSourceData = firstAccSelectedForTheGoalWithRecomendation
                ? fundingAccounts.find(
                      (el) => el.accountId === firstAccSelectedForTheGoalWithRecomendation?.accountNumber
                  )
                : initialFormValues;

            // Set touched state if the account has recurring contributions
            if (investmentSourceData?.recurringContributions && investmentSourceData.recurringContributions > 0) {
                setShowRecurringContributions(true); // Show section when there are contributions
            } else {
                setShowRecurringContributions(false); // Hide section when no contributions
            }

            let processedInvestmentData = investmentSourceData;

            // Logic previously gated by FF is now default
            // Check if we need to migrate from fundingYear to oneTimeFutureAmount
            const fundingDate = investmentSourceData?.fundingYear ? dayjs(investmentSourceData.fundingYear) : null;
            const hasFutureYear = !!fundingDate && isFundingDateInFuture(fundingDate);
            const hasOneTimeFutureAmount =
                investmentSourceData?.oneTimeFutureAmount !== undefined &&
                investmentSourceData.oneTimeFutureAmount >= 0;

            if (hasFutureYear && !hasOneTimeFutureAmount && investmentSourceData?.amount) {
                // Migration case: fundingYear is future year AND no oneTimeFutureAmount exists
                // Enable checkbox and create new object with migrated data
                setShowOneTimeFutureContribution(true);

                processedInvestmentData = {
                    ...investmentSourceData,
                    oneTimeFutureAmount: investmentSourceData.amount,
                    amount: 0
                } as InvestmentSourcesType;
            } else if (hasOneTimeFutureAmount) {
                // Normal case: has oneTimeFutureAmount, show checkbox
                setShowOneTimeFutureContribution(true);
            } else {
                // No future contribution data
                setShowOneTimeFutureContribution(false);
            }

            reset({
                ...processedInvestmentData,
                fundingYear:
                    processedInvestmentData?.fundingYear &&
                    isFundingDateInFuture(dayjs(processedInvestmentData.fundingYear))
                        ? dayjs(processedInvestmentData.fundingYear)
                        : null, // Keep empty for current/past month or if no funding month
                recurringContributionsEndDate: processedInvestmentData?.recurringContributionsEndDate
                    ? dayjs(processedInvestmentData?.recurringContributionsEndDate, 'DD-MM-YYYY').format('DD-MM-YYYY')
                    : undefined,
                recurringContributionsStartDate: processedInvestmentData?.recurringContributionsStartDate
                    ? dayjs(processedInvestmentData?.recurringContributionsStartDate, 'DD-MM-YYYY').format('DD-MM-YYYY')
                    : undefined
            });
            // Set the key states from existing investment data
            setRecurringContributionsStartDateKey(processedInvestmentData?.recurringContributionsStartDateKey || '');
            setRecurringContributionsEndDateKey(processedInvestmentData?.recurringContributionsEndDateKey || '');
        }
    }, [accountRecommendation]);

    // This function helps ensure that the WEALTH SOURCES selected in the goals remain selected.
    const getInvestmentSourceSelection = (accountId: string, goalInvestmentSources: any[]) => {
        const source = goalInvestmentSources.find((source) => source.accountNumber === accountId);

        // Return default values if source is undefined
        return {
            isSelected: source?.isSelected ?? false,
            isRCSelected: source?.isRCSelected ?? false
        };
    };

    const updateExistingGoals = (formFields: any[], investmentAccounts: InvestmentSourcesType[]) => {
        if (formFields && formFields?.length > 0) {
            formFields.forEach((item: any) => {
                const updatedInvestmentSources = item.data.sources.investmentSource;
                if (investmentAccounts.length > 0) {
                    investmentAccounts.forEach((acc) => {
                        const accountExistsIndex = updatedInvestmentSources.findIndex(
                            (source: any) => acc.accountId === source.accountNumber
                        );
                        if (accountExistsIndex !== -1) {
                            const { isSelected, isRCSelected } = getInvestmentSourceSelection(
                                acc.accountId,
                                updatedInvestmentSources
                            );

                            updatedInvestmentSources[accountExistsIndex] = {
                                accountNumber: acc.accountId,
                                accountName: acc.accountName,
                                isSelected,
                                isRCSelected,
                                accountBalance: acc.amount,
                                ...(acc.recurringContributions && {
                                    recurringContributions: acc.recurringContributions
                                }),
                                recurringContributionsEndDate: getYYYYMMDDfromDate(
                                    dayjs(acc.recurringContributionsEndDate)
                                ),
                                fundingYear: getYYYYMMDDfromDate(dayjs(acc.fundingYear)),
                                recurringContributionsStartDate: getYYYYMMDDfromDate(
                                    dayjs(acc.recurringContributionsStartDate)
                                ),
                                disabled: updatedInvestmentSources[accountExistsIndex].disabled || false
                            };
                        }
                    });
                }
            });
        }
        return formFields;
    };

    const noUserValuesEntered = () => {
        let data = getValues();
        const userValues = Object.values(data);
        const defaultValues = Object.values(initialFormValues);
        // check if there is difference between user entered values and default values
        return JSON.stringify(userValues) === JSON.stringify(defaultValues);
    };

    const onDeleteAccount = async (deletedAccounts: string[] = [], formFields?: any[]) => {
        dispatch(updateGlobalLoaderState(true));
        let originalClientData: typeof adviceClient = JSON.parse(JSON.stringify(adviceClient));
        const updatedClientData = Object.assign({}, originalClientData);
        updatedClientData['accounts'] = {
            ...updatedClientData['accounts'],
            investmentsSources: fundingAccounts.filter(
                (source: any) => !deletedAccountIds.includes(source.accountNumber)
            )
        };
        if (checkFFEnabled(FEATURE_NAMES['BACKEND_MANAGED_ID_TIMESTAMPS_FE_AEX-3021'])) {
            updatedClientData['accounts']['investmentsSources'] = (
                updatedClientData['accounts']['investmentsSources'] || []
            ).map(({ updatedAt, createdAt, ...rest }: any) =>
                willCallUpdateClientProfile && rest.accountId && rest.update !== true
                    ? { ...rest, update: false }
                    : { ...rest }
            );
            updatedClientData['accounts']['incomeSources'] = (updatedClientData['accounts']['incomeSources'] || []).map(
                ({ updatedAt, createdAt, ...rest }: any) =>
                    willCallUpdateClientProfile && rest.accountId && rest.update !== true
                        ? { ...rest, update: false }
                        : { ...rest }
            );
        }
        await dispatch(saveClient(updatedClientData));

        const formFieldsData = formFields || adviceGoals.formFields;
        if (formFieldsData.length > 0) {
            const newFormFields = formFieldsData.map((goal: any) => {
                return {
                    ...goal,
                    data: {
                        ...goal.data,
                        sources: {
                            ...goal.data.sources,
                            incomeSource: goal.data.sources.incomeSource.filter(
                                (source: any) => !deletedAccounts.includes(source.accountNumber)
                            ),
                            investmentSource: goal.data.sources.investmentSource.filter(
                                (source: any) => !deletedAccounts.includes(source.accountNumber)
                            )
                        }
                    }
                };
            });

            let updatedgoaldata = {
                ...{ ...adviceGoals, formFields: newFormFields },
                global: {
                    editMode: true,
                    ...adviceGoals?.global,
                    ...(adviceGoals.global?.enablePDFGenerate && { enablePDFGenerate: false, enablePlanDetails: false })
                }
            };
            if (goalsWithNoFundingSource?.length === 0 && adviceClient.step === 3) {
                await callRunpipe?.(
                    goeConfig?.reallocSchedules ?? [],
                    orgInfo?.orgEmail,
                    adviceClient,
                    adviceGoals,
                    dispatch,
                    updatedgoaldata,
                    updatedClientData
                );
            } else {
                updatedgoaldata.global.responseGoalCalculator = [];
                updatedgoaldata.global.status = STATUSVARIANTS.INCOMPLETE;
                updatedgoaldata.global.enablePDFGenerate = false;
                dispatch(setAffectedEwsTerms([...Object.values(EWS_DURATION_KEYS)]));
                await dispatch(syncAdviceGoals(updatedgoaldata));
            }
            handleReset();
            setOpenDrawer(false);
        }
        dispatch(updateGlobalLoaderState(false));
    };

    const submitFunds = async () => {
        try {
            let data = getValues();
            // Clear recurring contribution related fields only if user unchecked before CTA and FF enabled
            if (!showRecurringContributions) {
                data.recurringContributions = 0 as any;
                (data as any).frequency = '' as any;
                (data as any).recurringContributionsStartDate = null as any;
                (data as any).recurringContributionsEndDate = null as any;
            }
            // Clear one-time future contribution fields if user unchecked
            if (!showOneTimeFutureContribution) {
                data.oneTimeFutureAmount = undefined as any;
                (data as any).fundingYear = null as any;
            }
            if (noUserValuesEntered()) {
                // we just deleted an income source
                // checkUnsavedAndhandleClose(false);
                onDeleteAccount(deletedAccountIds, adviceGoals.formFields);
            } else {
                await trigger();
                if (!errors.accountName && !accountRecommendation) {
                    let isaccountName = false;
                    isaccountName = fundingAccounts
                        .filter((source) =>
                            currentMode === 'edit' && selectedAccountForEdit
                                ? getSourceKey(source) !== selectedAccountForEdit
                                : true
                        )
                        .some((i) => i.accountName.trim().toLowerCase() === data.accountName.trim().toLowerCase());
                    if (isaccountName) {
                        setError('accountName', {
                            type: 'required',
                            message: t('TEXT_ACCOUNT_NAME_EXIST')
                        });
                        return;
                    } else {
                        clearErrors('accountName');
                    }
                }
                if (!selectedAccountForEdit) {
                    // Generate accountId on FE for FF-disabled, and for non-flow2 (Advice) when FF is enabled
                    if (checkFFEnabled(FEATURE_NAMES['AEX-3590_3604_NEW_FLOW_UPDATE_SAVE_PLAN']) || !isFlow2) {
                        data['accountId'] = uuidv4();
                        // Mark as newly created on FE so it is never tagged with an `update` flag in the payload
                        if (checkFFEnabled(FEATURE_NAMES['BACKEND_MANAGED_ID_TIMESTAMPS_FE_AEX-3021']) && !isFlow2) {
                            data['isNew'] = true;
                        }
                    }
                }
                if (selectedAccountForEdit) {
                    if (checkFFEnabled(FEATURE_NAMES['BACKEND_MANAGED_ID_TIMESTAMPS_FE_AEX-3021'])) {
                        data['update'] = true;
                    }
                }
                data['recurringContributionsStartDate'] = data['recurringContributionsStartDate']
                    ? getYYYYMMDDfromDate(data['recurringContributionsStartDate'])
                    : undefined;
                data['recurringContributionsStartDateKey'] = recurringContributionsStartDateKey;
                data['recurringContributionsEndDate'] = data['recurringContributionsEndDate']
                    ? getYYYYMMDDfromDate(data['recurringContributionsEndDate'])
                    : undefined;
                data['recurringContributionsEndDateKey'] = recurringContributionsEndDateKey;
                const normalizeAmountField = (val: any) => Number(removeNonNumeric(val ?? 0) || 0);
                const amountVal = data['amount'];
                var formatedAmountVal = normalizeAmountField(amountVal);
                data['amount'] = formatedAmountVal;
                data['fundingYear'] = data['fundingYear'] ? getYYYYMMDDfromDate(dayjs(data['fundingYear'])) : undefined;
                const recConValue = data['recurringContributions'] ?? 0;
                var formatedrecConValue = normalizeAmountField(recConValue);
                data['recurringContributions'] = formatedrecConValue;
                if (data['oneTimeFutureAmount'] !== undefined) {
                    const oneTimeFutureAmountVal = data['oneTimeFutureAmount'];
                    var formatedOneTimeFutureAmountVal = normalizeAmountField(oneTimeFutureAmountVal);
                    data['oneTimeFutureAmount'] = formatedOneTimeFutureAmountVal;
                }
                if (!data.frequency && Number(removeNonNumeric(data.recurringContributions)) > 0) {
                    setError('frequency', {
                        type: 'required',
                        message: t('TEXT_THIS_IS_REQUIRED_FIELD')
                    });
                    return;
                } else {
                    clearErrors('frequency');
                }

                // Validate one-time future contribution fields when checkbox is enabled
                if (showOneTimeFutureContribution) {
                    // Funding year is mandatory when checkbox is enabled
                    if (!data.fundingYear) {
                        setError('fundingYear', {
                            type: 'required',
                            message: t('TEXT_THIS_IS_REQUIRED_FIELD')
                        });
                        return;
                    } else {
                        clearErrors('fundingYear');
                    }

                    // One-time future amount is mandatory when checkbox is enabled
                    if (data.oneTimeFutureAmount === undefined || data.oneTimeFutureAmount === null) {
                        setError('oneTimeFutureAmount', {
                            type: 'required',
                            message: t('TEXT_THIS_IS_REQUIRED_FIELD')
                        });
                        return;
                    } else if (data.oneTimeFutureAmount < 0) {
                        setError('oneTimeFutureAmount', {
                            type: 'min',
                            message: t('TEXT_MIN_VALUE')
                        });
                        return;
                    } else {
                        clearErrors('oneTimeFutureAmount');
                    }

                    // Funding month must be in the future when checkbox is enabled
                    const fundingDate = dayjs(data.fundingYear);
                    if (!isFundingDateInFuture(fundingDate)) {
                        setError('fundingYear', {
                            type: 'min',
                            message: t('TEXT_FUNDING_YEAR_MUST_BE_FUTURE')
                        });
                        return;
                    } else {
                        clearErrors('fundingYear');
                    }
                }

                if (isValid) {
                    let originalClientData: typeof adviceClient = JSON.parse(JSON.stringify(adviceClient));
                    const updatedClientData = Object.assign({}, originalClientData);
                    let originalGoalsData: typeof adviceGoals = JSON.parse(JSON.stringify(adviceGoals));
                    let updatedFormFields = [];
                    // check stages
                    // if (updatedClientData.accounts?.incomeSources?.length > 0) {
                    updatedClientData.stages = [...updatedClientData.stages, CLIENT_PROFILE_STAGE4];
                    // }
                    updatedClientData.error = false;
                    updatedClientData.loading = false;
                    const updatedGoalsData = Object.assign({}, originalGoalsData);
                    if (currentMode === 'edit' && selectedAccountForEdit) {
                        const mappedForEdit = fundingAccounts?.map((funding: any) =>
                            getSourceKey(funding) === selectedAccountForEdit
                                ? data
                                : willCallUpdateClientProfile &&
                                  checkFFEnabled(FEATURE_NAMES['BACKEND_MANAGED_ID_TIMESTAMPS_FE_AEX-3021']) &&
                                  funding.accountId &&
                                  funding.update !== true
                                ? { ...funding, update: false }
                                : funding
                        );
                        updatedClientData['accounts'] = {
                            ...updatedClientData['accounts'],
                            investmentsSources: mappedForEdit
                        };
                        updatedFormFields = updateExistingGoals(originalGoalsData.formFields, mappedForEdit);
                        if (!updatedGoalsData.global) {
                            updatedGoalsData.global = {};
                        }
                        updatedGoalsData.global.editMode = true;
                    } else {
                        const existingSources = checkFFEnabled(
                            FEATURE_NAMES['BACKEND_MANAGED_ID_TIMESTAMPS_FE_AEX-3021']
                        )
                            ? fundingAccounts.map((source: any) =>
                                  willCallUpdateClientProfile && source.accountId && source.update !== true
                                      ? { ...source, update: false }
                                      : source
                              )
                            : fundingAccounts;
                        updatedClientData['accounts'] = {
                            ...updatedClientData['accounts'],
                            investmentsSources: [...existingSources, data]
                        };
                        updatedFormFields = updateExistingGoals(originalGoalsData.formFields, [
                            ...existingSources,
                            data
                        ]);
                    }
                    if (checkFFEnabled(FEATURE_NAMES['BACKEND_MANAGED_ID_TIMESTAMPS_FE_AEX-3021'])) {
                        updatedClientData['accounts']['incomeSources'] = (
                            updatedClientData['accounts']['incomeSources'] || []
                        ).map(({ updatedAt, createdAt, ...rest }: any) =>
                            willCallUpdateClientProfile && rest.accountId && rest.update !== true
                                ? { ...rest, update: false }
                                : { ...rest }
                        );
                        updatedClientData['accounts']['investmentsSources'] = (
                            updatedClientData['accounts']['investmentsSources'] || []
                        ).map(({ updatedAt, createdAt, ...rest }: any) => rest);
                    }
                    updatedGoalsData.formFields.forEach((goal: { data: { sources: { investmentSource: any[] } } }) => {
                        goal.data.sources.investmentSource.forEach((goalInvestmentSrc: any) => {
                            updatedClientData.accounts.investmentsSources.forEach((investmentsrc: any) => {
                                if (investmentsrc.accountId === goalInvestmentSrc.accountNumber) {
                                    goalInvestmentSrc.accountBalance = investmentsrc.amount;
                                    goalInvestmentSrc.accountName = investmentsrc.sourceName;
                                    goalInvestmentSrc.frequency = investmentsrc.frequency;
                                    goalInvestmentSrc.fundingYear = getYYYYMMDDfromDate(
                                        dayjs(investmentsrc.fundingYear)
                                    );
                                    goalInvestmentSrc.recurringContributions = investmentsrc.recurringContributions;
                                    goalInvestmentSrc.recurringContributionsEndDate = getYYYYMMDDfromDate(
                                        dayjs(investmentsrc.recurringContributionsEndDate)
                                    );
                                    goalInvestmentSrc.recurringContributionsStartDate = getYYYYMMDDfromDate(
                                        dayjs(investmentsrc.recurringContributionsStartDate)
                                    );
                                }
                            });
                        });
                    });

                    await dispatch(saveClient(updatedClientData));
                    // await dispatch(updateClientInvestmentSources(updatedClientData?.accounts?.investmentsSources));
                    if (
                        currentMode === 'edit' &&
                        onEditSuccess &&
                        (!noUserValuesEntered() || investmentSourceEdited) &&
                        deletedAccountIds?.length > 0
                    ) {
                        onEditSuccess(deletedAccountIds, updatedFormFields);
                    }
                    if (updatedGoalsData.formFields?.length > 0) {
                        if (!updatedGoalsData.global) {
                            updatedGoalsData.global = {};
                        }
                        if (updatedGoalsData.global.hasRunPipe) {
                            updatedGoalsData.global.status = 'incomplete';
                        }
                        if (updatedGoalsData.global?.enablePDFGenerate) {
                            updatedGoalsData.global.enablePDFGenerate = false;
                        }

                        if (updatedGoalsData.global?.enablePlanDetails) {
                            updatedGoalsData.global.enablePlanDetails = false;
                        }
                        if (
                            (goalsWithNoFundingSource?.length === 0 || !goalsWithNoFundingSource) &&
                            adviceClient.step === 3
                        ) {
                            dispatch(updateGlobalLoaderState(true));
                            await callRunpipe?.(
                                goeConfig?.reallocSchedules ?? [],
                                orgInfo?.orgEmail,
                                adviceClient,
                                adviceGoals,
                                dispatch,
                                updatedGoalsData,
                                updatedClientData
                            );
                            dispatch(updateGlobalLoaderState(false));
                        } else {
                            updatedGoalsData.response = [];
                            updatedGoalsData.global.responseGoalCalculator = [];
                            updatedGoalsData.global.status = STATUSVARIANTS.INCOMPLETE;
                            updatedGoalsData.global.enablePDFGenerate = false;
                            dispatch(setAffectedEwsTerms([...Object.values(EWS_DURATION_KEYS)]));
                            // }
                            // await dispatch(updateGoal(updatedGoalsData));
                            await dispatch(syncAdviceGoals(updatedGoalsData));
                        }
                    }
                    handleReset();
                    setSelectedAccountForEdit('');
                    setOpenDrawer(false);
                    setFundingAccounts([]);
                }
            }
        } catch (error) {
            console.error('Error in submitting funds', error);
            setApiErrorToken((prev) => prev + 1); // Trigger error message display in UI
        }
    };

    const accountHolderOptions = useMemo(() => {
        if (relationships.length === 0) {
            setValue('accountHolder', 'primary');
            // show only primary
            return [
                {
                    label: `${clientFirstName} ${clientLastName}`,
                    value: `primary`,
                    disabled: true
                }
            ];
        }
        const holders = relationships.map(({ firstName, lastName, id }) => {
            return {
                label: `${firstName} ${lastName}`,
                value: id,
                disabled: false
            };
        });
        holders.unshift({
            label: `${clientFirstName} ${clientLastName}`,
            value: `primary`,
            disabled: false
        });
        const hasClient = !!clientFirstName && !!clientLastName;
        const hasCoClient = relationships.length > 0;

        if (hasClient && hasCoClient) {
            holders.push({
                label: t('TEXT_JOINT'),
                value: 'joint',
                disabled: false
            });
        }
        return holders;
    }, [relationships, clientFirstName, clientLastName]);

    const handleRecurringContributionsChange = (selectedValue: string) => {
        const numericValue = selectedValue.replace(/\D/g, '');

        if (!numericValue) {
            setValue('recurringContributions', '' as unknown as number, {
                shouldValidate: true,
                shouldDirty: true,
                shouldTouch: true
            });
            trigger('amount');
            return;
        }

        const parsedValue = Number(numericValue);
        setValue('recurringContributions', parsedValue, {
            shouldValidate: true,
            shouldDirty: true,
            shouldTouch: true
        });
        trigger('amount');
    };

    const handleAmountChange = (value: string) => {
        const numericString = value.replace(/\D/g, '');
        if (!numericString) {
            setValue('amount', '' as unknown as number, {
                shouldValidate: true,
                shouldDirty: true
            });
            return;
        }

        const numericValue = Number(numericString);
        setValue('amount', numericValue, {
            shouldValidate: true,
            shouldDirty: true,
            shouldTouch: true
        });
    };

    const handleOneTimeFutureAmountChange = (value: string) => {
        const numericString = value.replace(/\D/g, '');
        if (!numericString) {
            setValue('oneTimeFutureAmount', '' as unknown as number, {
                shouldValidate: true,
                shouldDirty: true,
                shouldTouch: true
            });
            trigger('amount');
            return;
        }

        const numericValue = Number(numericString);
        setValue('oneTimeFutureAmount', numericValue, {
            shouldValidate: true,
            shouldDirty: true,
            shouldTouch: true
        });
        trigger('amount');
    };

    const addAnotherFund = async (data: InvestmentSourcesType) => {
        // Clear recurring contribution related fields only if user unchecked before CTA and FF enabled
        if (!showRecurringContributions) {
            data.recurringContributions = 0 as any;
            (data as any).frequency = '' as any;
            (data as any).recurringContributionsStartDate = undefined as any;
            (data as any).recurringContributionsEndDate = undefined as any;
        }
        // Clear one-time future contribution fields if user unchecked
        if (!showOneTimeFutureContribution) {
            data.oneTimeFutureAmount = undefined as any;
            (data as any).fundingYear = undefined as any;
        }
        let isaccountName = false;
        isaccountName = fundingAccounts
            .filter((source) =>
                currentMode === 'edit' && selectedAccountForEdit
                    ? getSourceKey(source) !== selectedAccountForEdit
                    : true
            )
            .some((i) => i.accountName.trim().toLowerCase() === data.accountName.trim().toLowerCase());
        if (isaccountName) {
            setError('accountName', { type: 'required', message: t('TEXT_ACCOUNT_NAME_EXIST') });
            return;
        } else {
            clearErrors('accountName');
        }

        // Validate one-time future contribution fields when checkbox is enabled
        if (showOneTimeFutureContribution) {
            // Funding year is mandatory when checkbox is enabled
            if (!data.fundingYear) {
                setError('fundingYear', {
                    type: 'required',
                    message: t('TEXT_THIS_IS_REQUIRED_FIELD')
                });
                return;
            } else {
                clearErrors('fundingYear');
            }

            // One-time future amount is mandatory when checkbox is enabled
            if (
                data.oneTimeFutureAmount === undefined ||
                data.oneTimeFutureAmount === null ||
                String(data.oneTimeFutureAmount).trim() === ''
            ) {
                setError('oneTimeFutureAmount', {
                    type: 'required',
                    message: t('TEXT_THIS_IS_REQUIRED_FIELD')
                });
                return;
            } else if (data.oneTimeFutureAmount < 0) {
                setError('oneTimeFutureAmount', {
                    type: 'min',
                    message: t('TEXT_MIN_VALUE')
                });
                return;
            } else {
                clearErrors('oneTimeFutureAmount');
            }

            // Funding month must be in the future when checkbox is enabled
            const fundingDate = dayjs(data.fundingYear);
            if (!isFundingDateInFuture(fundingDate)) {
                setError('fundingYear', {
                    type: 'min',
                    message: t('TEXT_FUNDING_YEAR_MUST_BE_FUTURE')
                });
                return;
            } else {
                clearErrors('fundingYear');
            }
        }

        if (!selectedAccountForEdit) {
            // Generate accountId on FE for FF-disabled, and for non-flow2 (Advice) when FF is enabled
            if (checkFFEnabled(FEATURE_NAMES['AEX-3590_3604_NEW_FLOW_UPDATE_SAVE_PLAN']) || !isFlow2) {
                data['accountId'] = uuidv4();
                // Mark as newly created on FE so it is never tagged with an `update` flag in the payload
                if (checkFFEnabled(FEATURE_NAMES['BACKEND_MANAGED_ID_TIMESTAMPS_FE_AEX-3021']) && !isFlow2) {
                    data['isNew'] = true;
                }
            }
        }
        if (selectedAccountForEdit) {
            if (checkFFEnabled(FEATURE_NAMES['BACKEND_MANAGED_ID_TIMESTAMPS_FE_AEX-3021'])) {
                data['update'] = true;
            }
        }
        data['recurringContributionsStartDate'] = data['recurringContributionsStartDate']
            ? getYYYYMMDDfromDate(data['recurringContributionsStartDate'])
            : undefined;
        data['recurringContributionsEndDate'] = data['recurringContributionsEndDate']
            ? getYYYYMMDDfromDate(data['recurringContributionsEndDate'])
            : undefined;
        const normalizeAmountField = (val: any) => Number(removeNonNumeric(val ?? 0) || 0);
        let amountVal = data['amount'];
        var formatedAmountVal = normalizeAmountField(amountVal);
        data['amount'] = formatedAmountVal;
        data['fundingYear'] = data['fundingYear'] ? getYYYYMMDDfromDate(dayjs(data['fundingYear'])) : undefined;
        const recConValue = data['recurringContributions'];
        var formatedrecConValue = normalizeAmountField(recConValue);
        data['recurringContributions'] = formatedrecConValue;
        if (data['oneTimeFutureAmount'] !== undefined) {
            const oneTimeFutureAmountVal = data['oneTimeFutureAmount'];
            var formatedOneTimeFutureAmountVal = normalizeAmountField(oneTimeFutureAmountVal);
            data['oneTimeFutureAmount'] = formatedOneTimeFutureAmountVal;
        }
        let fundingSrc;
        if (currentMode === 'edit' && selectedAccountForEdit) {
            let currentFuncdingAccounts = [...fundingAccounts];
            currentFuncdingAccounts = currentFuncdingAccounts.map((source) =>
                getSourceKey(source) === selectedAccountForEdit
                    ? data
                    : willCallUpdateClientProfile &&
                      checkFFEnabled(FEATURE_NAMES['BACKEND_MANAGED_ID_TIMESTAMPS_FE_AEX-3021']) &&
                      source.accountId &&
                      source.update !== true
                    ? { ...source, update: false }
                    : source
            );

            fundingSrc = currentFuncdingAccounts;
            setFundingAccounts(currentFuncdingAccounts);
        } else {
            const existingSources = checkFFEnabled(FEATURE_NAMES['BACKEND_MANAGED_ID_TIMESTAMPS_FE_AEX-3021'])
                ? fundingAccounts.map((source) =>
                      willCallUpdateClientProfile && source.accountId && source.update !== true
                          ? { ...source, update: false }
                          : source
                  )
                : fundingAccounts;
            fundingSrc = [...existingSources, data];
            setFundingAccounts([...existingSources, data]);
        }
        reset(initialFormValues);
        const copyInvestment = Object.assign(adviceClient.accounts.investmentsSources);
        if (
            !selectedAccountForEdit ||
            !noUserValuesEntered() ||
            !(JSON.stringify(copyInvestment) === JSON.stringify(fundingSrc))
        )
            setInvestmentSourceEdited(true);
        setSelectedAccountForEdit('');

        setSelectedAccountInDropdown(''); // Reset dropdown selection when switching to add mode
        setKeyId(uuidv4());
        setCurrentMode('add');
    };

    const persistUnsavedFundingAccounts = async () => {
        if (!errors.accountName) {
            let isaccountName = false;
            isaccountName = fundingAccounts
                .filter((source) =>
                    currentMode === 'edit' && selectedAccountForEdit
                        ? getSourceKey(source) !== selectedAccountForEdit
                        : true
                )
                .some((i) => i.accountName.trim().toLowerCase() === getValues('accountName').trim().toLowerCase());
            if (isaccountName) {
                setError('accountName', {
                    type: 'required',
                    message: t('TEXT_ACCOUNT_NAME_EXIST')
                });
                return;
            } else {
                clearErrors('accountName');
            }
        }
        if (investmentSourceEdited) {
            try {
                dispatch(updateGlobalLoaderState(true));
                let originalClientData: typeof adviceClient = JSON.parse(JSON.stringify(adviceClient));
                const updatedClientData = Object.assign({}, originalClientData);
                let originalGoalsData: typeof adviceGoals = JSON.parse(JSON.stringify(adviceGoals));
                updatedClientData.error = false;
                updatedClientData.loading = false;
                updatedClientData['accounts'] = {
                    ...updatedClientData['accounts'],
                    investmentsSources: fundingAccounts.filter(
                        (source: any) => !deletedAccountIds.includes(source.accountNumber)
                    )
                };
                if (checkFFEnabled(FEATURE_NAMES['BACKEND_MANAGED_ID_TIMESTAMPS_FE_AEX-3021'])) {
                    updatedClientData['accounts']['incomeSources'] = (
                        updatedClientData['accounts']['incomeSources'] || []
                    ).map(({ updatedAt, createdAt, ...rest }: any) =>
                        willCallUpdateClientProfile && rest.accountId && rest.update !== true
                            ? { ...rest, update: false }
                            : { ...rest }
                    );
                    updatedClientData['accounts']['investmentsSources'] = (
                        updatedClientData['accounts']['investmentsSources'] || []
                    ).map(({ updatedAt, createdAt, ...rest }: any) =>
                        willCallUpdateClientProfile && rest.accountId && rest.update !== true
                            ? { ...rest, update: false }
                            : { ...rest }
                    );
                }

                updatedClientData.stages = [...updatedClientData.stages, CLIENT_PROFILE_STAGE4];
                await dispatch(saveClient(updatedClientData));
                await dispatch(updateClientInvestmentSources(updatedClientData.accounts.investmentsSources));

                const updatedGoalsData = Object.assign({}, originalGoalsData);
                if (updatedGoalsData.formFields.length > 0) {
                    updatedGoalsData.formFields.forEach((item: { data: { sources: { investmentSource: any } } }) => {
                        const goalInvestmentSource = item.data.sources.investmentSource;
                        const investmentSource: any = [];
                        updatedClientData['accounts']['investmentsSources'].forEach(
                            (itemData: {
                                accountId: string;
                                accountName: any;
                                amount: any;
                                recurringContributions: any;
                                recurringContributionsStartDate:
                                    | string
                                    | number
                                    | Date
                                    | dayjs.Dayjs
                                    | null
                                    | undefined;
                                recurringContributionsEndDate: string | number | Date | dayjs.Dayjs | null | undefined;
                                fundingYear: string | number | Date | dayjs.Dayjs | null | undefined;
                            }) => {
                                const { isSelected, isRCSelected } = getInvestmentSourceSelection(
                                    itemData.accountId,
                                    goalInvestmentSource
                                );
                                investmentSource.push({
                                    accountNumber: itemData.accountId,
                                    accountName: itemData.accountName,
                                    isSelected,
                                    isRCSelected,
                                    accountBalance: itemData.amount,
                                    recurringContributions: itemData.recurringContributions,
                                    recurringContributionsStartDate: getYYYYMMDDfromDate(
                                        dayjs(itemData.recurringContributionsStartDate)
                                    ),
                                    recurringContributionsEndDate: getYYYYMMDDfromDate(
                                        dayjs(itemData.recurringContributionsEndDate)
                                    ),
                                    fundingYear: getYYYYMMDDfromDate(dayjs(itemData.fundingYear))
                                });
                            }
                        );
                        item.data.sources.investmentSource = investmentSource;
                    });

                    if (isPlanRun && investmentSourceEdited) {
                        updatedGoalsData.formFields.forEach(
                            (goal: { data: { sources: { investmentSource: any[] } } }) => {
                                goal.data.sources.investmentSource.forEach((goalInvestmentSrc: any) => {
                                    updatedClientData.accounts.investmentsSources.forEach((investmentsrc: any) => {
                                        if (investmentsrc.accountId === goalInvestmentSrc.accountNumber) {
                                            goalInvestmentSrc.accountBalance =
                                                investmentsrc.frequency === 'Monthly'
                                                    ? investmentsrc.amount * 12
                                                    : investmentsrc.amount;
                                            goalInvestmentSrc.accountName = investmentsrc.sourceName;
                                            goalInvestmentSrc.fundingYear = getYYYYMMDDfromDate(
                                                dayjs(investmentsrc.fundingYear)
                                            );
                                            goalInvestmentSrc.frequency = investmentsrc.frequency;
                                            goalInvestmentSrc.recurringContributions =
                                                investmentsrc.recurringContributions;
                                            goalInvestmentSrc.recurringContributionsEndDate = getYYYYMMDDfromDate(
                                                dayjs(investmentsrc.recurringContributionsEndDate)
                                            );
                                            goalInvestmentSrc.recurringContributionsStartDate = getYYYYMMDDfromDate(
                                                dayjs(investmentsrc.recurringContributionsStartDate)
                                            );
                                        }
                                    });
                                });
                            }
                        );
                        if (updatedGoalsData.global.hasRunPipe) {
                            updatedGoalsData.global.status = 'incomplete';
                        }
                        if (deletedAccountIds.length > 0) {
                            // remove the deleted account from any client goals associated with it
                            updatedGoalsData.formFields.forEach(
                                (goal: { data: { sources: { investmentSource: any[] } } }) => {
                                    goal.data.sources.investmentSource = goal.data.sources.investmentSource.filter(
                                        (source: any) => !deletedAccountIds.includes(source.accountNumber)
                                    );
                                }
                            );
                            updatedGoalsData.global.enablePDFGenerate = false;
                        }
                    }
                    if (updatedGoalsData.global?.enablePDFGenerate) {
                        updatedGoalsData.global.enablePDFGenerate = false;
                    }
                    if (updatedGoalsData.global?.enablePlanDetails) {
                        updatedGoalsData.global.enablePlanDetails = false;
                    }
                    if (goalsWithNoFundingSource?.length === 0 && adviceClient.step === 3 && investmentSourceEdited) {
                        await callRunpipe?.(
                            goeConfig?.reallocSchedules ?? [],
                            orgInfo?.orgEmail,
                            adviceClient,
                            adviceGoals,
                            dispatch,
                            updatedGoalsData,
                            updatedClientData
                        );
                    } else {
                        updatedGoalsData.response = [];
                        updatedGoalsData.global.responseGoalCalculator = [];
                        if (isFlow2) updatedGoalsData.global.status = STATUSVARIANTS.INCOMPLETE;
                        updatedGoalsData.global.enablePDFGenerate = false;
                        dispatch(setAffectedEwsTerms([...Object.values(EWS_DURATION_KEYS)]));
                        // }
                        await dispatch(syncAdviceGoals(updatedGoalsData));
                    }
                }
                handleReset();
                setOpenDrawer(false);
                dispatch(updateGlobalLoaderState(false));
            } catch (error) {
                console.error('Error in submitting funds', error);
                dispatch(updateGlobalLoaderState(false));
            }
        }
    };
    // const showDollarSign = currencySymbol === 'en' || currencySymbol === 'es';
    const checkUnsavedAndhandleClose = async (flag: boolean) => {
        await persistUnsavedFundingAccounts();
        handleReset();
        dispatch(clearClientError());
        setOpenDrawer(flag);
    };

    useEffect(() => {
        const locationValue = orgInfo?.investmentAccountLocation?.['en']?.[0]?.value;
        if (locationValue) setValue('location', locationValue);
    }, []);

    useLayoutEffect(() => {
        // Handle frequency changes to convert existing selections appropriately
        // Only apply when there's already a value selected (prevents auto-selection on first frequency change)
        if (frequency) {
            const currentValue = getValues('recurringContributionsStartDate');

            // Only update if there's already a value selected AND it's not null/undefined
            if (currentValue && currentValue !== null && currentValue !== undefined) {
                // Handle different formats - could be dayjs object, string, or date
                let currentDateFormatted;
                if (typeof currentValue === 'string') {
                    // If it's already a string, try to parse it
                    currentDateFormatted = dayjs(currentValue, 'DD-MM-YYYY').format('DD-MM-YYYY');
                } else if (dayjs.isDayjs(currentValue)) {
                    // If it's a dayjs object
                    currentDateFormatted = currentValue.format('DD-MM-YYYY');
                } else {
                    // Try to parse as regular date
                    currentDateFormatted = dayjs(currentValue).format('DD-MM-YYYY');
                }

                if (!isMonthYearDatesEnabled() && frequency === 'Annually') {
                    // Check if current value is the "next month" default from Monthly frequency
                    const nextMonthDefault = dayjs().add(1, 'month').startOf('month').format('DD-MM-YYYY');
                    if (currentDateFormatted === nextMonthDefault) {
                        // Convert from "next month" to "next year"
                        setValue('recurringContributionsStartDate', dayjs().add(1, 'year'));
                    }
                } else if (frequency === 'Monthly') {
                    // Check if current value is the "next year" default from Annual frequency
                    const nextYearDefault = dayjs().add(1, 'year').format('DD-MM-YYYY');
                    if (currentDateFormatted === nextYearDefault) {
                        // Convert from "next year" to "next month"
                        setValue('recurringContributionsStartDate', dayjs().add(1, 'month').startOf('month'));
                    }
                }
            }
        }
    }, [frequency, setValue, getValues]);

    const handleEditClick = (accountId: string) => {
        // Set the dropdown selection when editing an account
        setSelectedAccountInDropdown(accountId);

        // Check if this is for goal recommendation
        if (accountId === goalDataRecomendation?.originalData?.formFields?.sources?.investmentSource[0]?.accountNumber)
            setAccountRecommendation(goalDataRecomendation);
        else {
            setAccountRecommendation(undefined);
            setSelectedAccountForEdit(accountId);
            const investmentSourceData = fundingAccounts.find((el) => getSourceKey(el) === accountId);

            // Set touched state & checkbox visibility based on existing recurring contributions
            if (investmentSourceData?.recurringContributions && investmentSourceData.recurringContributions > 0) {
                setShowRecurringContributions(true); // ensure section is visible when editing
            } else {
                setShowRecurringContributions(false);
            }

            let processedInvestmentData = investmentSourceData;

            // Check if we need to migrate from fundingYear to oneTimeFutureAmount
            const fundingDate = investmentSourceData?.fundingYear ? dayjs(investmentSourceData.fundingYear) : null;
            const hasFutureYear = !!fundingDate && isFundingDateInFuture(fundingDate);
            const hasOneTimeFutureAmount =
                investmentSourceData?.oneTimeFutureAmount !== undefined &&
                investmentSourceData.oneTimeFutureAmount >= 0;

            if (hasFutureYear && !hasOneTimeFutureAmount && investmentSourceData?.amount) {
                // Migration case: fundingYear is future year AND no oneTimeFutureAmount exists
                // Enable checkbox and create new object with migrated data
                setShowOneTimeFutureContribution(true);

                processedInvestmentData = {
                    ...investmentSourceData,
                    oneTimeFutureAmount: investmentSourceData.amount,
                    amount: 0
                } as InvestmentSourcesType;
            } else if (hasOneTimeFutureAmount) {
                // Normal case: has oneTimeFutureAmount, show checkbox
                setShowOneTimeFutureContribution(true);
            } else {
                // No future contribution data
                setShowOneTimeFutureContribution(false);
            }
            reset({
                ...processedInvestmentData,
                // oneTimeFutureAmount:
                //     processedInvestmentData?.oneTimeFutureAmount === 0
                //         ? undefined
                //         : processedInvestmentData?.oneTimeFutureAmount,
                fundingYear:
                    processedInvestmentData?.fundingYear &&
                    isFundingDateInFuture(dayjs(processedInvestmentData.fundingYear))
                        ? dayjs(processedInvestmentData.fundingYear)
                        : null, // Keep empty for current/past month or if no funding month
                recurringContributionsEndDate: processedInvestmentData?.recurringContributionsEndDate
                    ? dayjs(processedInvestmentData?.recurringContributionsEndDate, 'DD-MM-YYYY').format('DD-MM-YYYY')
                    : undefined,
                recurringContributionsStartDate: processedInvestmentData?.recurringContributionsStartDate
                    ? dayjs(processedInvestmentData?.recurringContributionsStartDate, 'DD-MM-YYYY').format('DD-MM-YYYY')
                    : undefined
            });
            // Set the key states from existing investment data
            setRecurringContributionsStartDateKey(processedInvestmentData?.recurringContributionsStartDateKey || '');
            setRecurringContributionsEndDateKey(processedInvestmentData?.recurringContributionsEndDateKey || '');
        }
        setCurrentMode('edit');
    };

    const handleDeleteClick = (accountId: string) => {
        setSelectedAccountForEdit(accountId);
        setDeleteModalState(true);
    };

    const handleConfirmDelete = async () => {
        handleReset();
        const updatedSources = fundingAccounts.filter((source) => getSourceKey(source) !== selectedAccountForEdit);
        const newDeletedAccountIds = [...deletedAccountIds, selectedAccountForEdit];
        setDeletedAccountIds(newDeletedAccountIds);
        setFundingAccounts(updatedSources);
        setDeleteModalState(!deleteModalState);
        setSelectedAccountForEdit('');
        // Reset dropdown selection and switch to add mode after deletion

        setSelectedAccountInDropdown('');
        setCurrentMode('add');
        setInvestmentSourceEdited(true);
        setIsDelete(true);
    };

    // Handle mode changes from parent component (AccountsCardFF)
    useEffect(() => {
        if (mode === 'edit' && selectedAccountId) {
            setSelectedAccountForEdit(selectedAccountId);
            setSelectedAccountInDropdown(selectedAccountId); // Set dropdown selection for edit mode
            setCurrentMode('edit');
            // Populate form with account data
            const investmentSourceData = fundingAccounts.find((el) => getSourceKey(el) === selectedAccountId);
            if (investmentSourceData) {
                if (investmentSourceData?.recurringContributions && investmentSourceData.recurringContributions > 0) {
                    setShowRecurringContributions(true); // Show section when there are contributions
                } else {
                    setShowRecurringContributions(false); // Hide section when no contributions
                }

                let processedInvestmentData = investmentSourceData;

                // Check if we need to migrate from fundingYear to oneTimeFutureAmount
                const fundingDate = investmentSourceData?.fundingYear ? dayjs(investmentSourceData.fundingYear) : null;
                const hasFutureYear = !!fundingDate && isFundingDateInFuture(fundingDate);
                const hasOneTimeFutureAmount =
                    investmentSourceData?.oneTimeFutureAmount !== undefined &&
                    investmentSourceData.oneTimeFutureAmount >= 0;

                if (hasFutureYear && !hasOneTimeFutureAmount && investmentSourceData?.amount) {
                    // Migration case: fundingYear is future year AND no oneTimeFutureAmount exists
                    // Enable checkbox and create new object with migrated data
                    setShowOneTimeFutureContribution(true);

                    processedInvestmentData = {
                        ...investmentSourceData,
                        oneTimeFutureAmount: investmentSourceData.amount,
                        amount: 0
                    } as InvestmentSourcesType;
                } else if (hasOneTimeFutureAmount) {
                    // Normal case: has oneTimeFutureAmount, show checkbox
                    setShowOneTimeFutureContribution(true);
                } else {
                    // No future contribution data
                    setShowOneTimeFutureContribution(false);
                }
                reset({
                    ...processedInvestmentData,
                    fundingYear:
                        processedInvestmentData?.fundingYear &&
                        isFundingDateInFuture(dayjs(processedInvestmentData.fundingYear))
                            ? dayjs(processedInvestmentData.fundingYear)
                            : null, // Keep empty for current/past month or if no funding month
                    recurringContributionsStartDate: processedInvestmentData?.recurringContributionsStartDate
                        ? dayjs(processedInvestmentData.recurringContributionsStartDate, 'DD-MM-YYYY').format(
                              'DD-MM-YYYY'
                          )
                        : undefined,
                    recurringContributionsEndDate: processedInvestmentData?.recurringContributionsEndDate
                        ? dayjs(processedInvestmentData.recurringContributionsEndDate, 'DD-MM-YYYY').format(
                              'DD-MM-YYYY'
                          )
                        : undefined
                });
            }
        } else if (mode === 'add') {
            setSelectedAccountForEdit('');
            setCurrentMode('add');
            handleReset(); // Reset form to initial values
        }
    }, [mode, selectedAccountId, fundingAccounts]);

    // Handle internal mode changes (like from "Save & Add Another")
    useEffect(() => {
        if (currentMode === 'add' && !selectedAccountForEdit) {
            setCurrentMode('add');
            // Reset form when switching to add mode internally
            handleReset();
        }
    }, [currentMode, selectedAccountForEdit]);

    // Handle recurring contributions checkbox state changes
    useEffect(() => {
        const amountValue = getValues('amount');
        const hasAmountValue = amountValue !== undefined && amountValue !== null && String(amountValue).trim() !== '';
        if ((formState.touchedFields.amount && hasAmountValue) || errors.amount) {
            trigger('amount');
        }
    }, [showRecurringContributions]);

    useEffect(() => {
        const amountValue = getValues('amount');
        const hasAmountValue = amountValue !== undefined && amountValue !== null && String(amountValue).trim() !== '';
        if ((formState.touchedFields.amount && hasAmountValue) || errors.amount) {
            trigger('amount');
        }
    }, [showOneTimeFutureContribution]);

    const handleCancelDelete = async () => {
        setDeleteModalState(!deleteModalState);
    };
    const handleDateChange = (
        value: dayjs.Dayjs,
        // eslint-disable-next-line no-unused-vars
        onChange: (val: dayjs.Dayjs | undefined) => void,
        fieldName: string
    ) => {
        if (!value || !value.isValid()) {
            onChange(undefined); // <-- triggers validation for cleared/invalid input
            return;
        }
        if (!isMonthYearDatesEnabled() && frequency === 'Annually') {
            onChange(value?.startOf('year'));
        } else {
            onChange(value?.startOf('month'));
        }

        if (fieldName === 'recurringContributionsStartDate') {
            const endValue = getValues('recurringContributionsEndDate');
            if (endValue) {
                trigger('recurringContributionsEndDate');
                trigger('frequency');
            }
        }
        // fundingYear
        if (fieldName === 'fundingYear') {
            trigger('fundingYear');
        }
    };

    const isDeleteClicked = () => {
        if (isDelete) {
            return !isDelete;
        } else {
            return !isFormValid();
        }
    };

    const formValues = watch();
    // Function to check if form is edited
    const checkIfFormIsEdited = () => {
        const fundingSource = fundingAccounts.find((source) => getSourceKey(source) == selectedAccountForEdit);
        let data = getValues();
        let amountVal = data['amount'];
        var stringAmountVal = Number(removeNonNumeric(amountVal));
        data['amount'] = stringAmountVal;
        data['fundingYear'] = data['fundingYear'] ? getYYYYMMDDfromDate(dayjs(data['fundingYear'])) : null;
        if (data?.recurringContributionsStartDate != null && data?.recurringContributionsEndDate != null) {
            data['recurringContributionsStartDate'] = getYYYYMMDDfromDate(data['recurringContributionsStartDate']);
            data['recurringContributionsEndDate'] = getYYYYMMDDfromDate(data['recurringContributionsEndDate']);
        }
        return currentMode === 'edit' && fundingSource
            ? !(JSON.stringify(removeNullUndefined(fundingSource)) === JSON.stringify(removeNullUndefined(data)))
            : !isEqual(initialFormValues, {
                  ...formValues,
                  amount:
                      formValues['amount'] === undefined || formValues['amount'] === 0
                          ? undefined
                          : formValues['amount']
              });
    };

    const generateBannerMessageFromAppliedRecommendation = () => {
        if (accountRecommendation?.oneTimeTopUp)
            return t('ACC_DRAWER_BANNER_LUMPSUM_MESSAGE', {
                lumpsumDrawerBannerAmount: formatCurrency(accountRecommendation?.oneTimeTopUp)
            });
        if (accountRecommendation?.topUpAccumulation)
            return t('ACC_DRAWER_BANNER_ANN_CONT_MESSAGE', {
                annContDrawerBannerAmount: formatCurrency(accountRecommendation?.topUpAccumulation)
            });
        if (accountRecommendation?.recomendedTenure)
            return t('ACC_DRAWER_BANNER_REC_TENURE_MESSAGE', {
                recTenDrawerBannerFromDate: generateDateLabelFromFrequency(
                    accountRecommendation?.originalData?.formFields?.sources?.investmentSource[0]
                        ?.recurringContributionsEndDate,
                    0,
                    'MMM YYYY'
                ),
                recTenDrawerBannerToDate: generateDateLabelFromFrequency(
                    accountRecommendation?.originalData?.formFields?.sources?.investmentSource[0]
                        ?.recurringContributionsEndDate,
                    accountRecommendation?.recomendedTenure,
                    'MMM YYYY'
                )
            });
    };

    const generateDateLabelFromFrequency = (dateLabel: string, yearsToAdd?: number, format?: string) => {
        // console.log(
        //     dayjs(dateLabel)
        //         .add(yearsToAdd || 0, 'years')
        //         .format('YYYY')
        // );
        // console.log(yearsToAdd);
        if (!isMonthYearDatesEnabled() && frequency === 'Annually')
            return dayjs(dateLabel)
                .add(yearsToAdd || 0, 'years')
                .format('YYYY');
        return dayjs(dateLabel)
            .add(yearsToAdd || 0, 'years')
            .format(format || 'MM/YYYY');
    };

    // Custom validation function for recurring contributions
    const isRecurringContributionsValid = () => {
        // If recurring contributions checkbox is not checked, no additional validation needed
        if (!showRecurringContributions) {
            return true;
        }

        // If recurring contributions is checked, all fields inside the container must be valid
        const recurringContributionsValue = getValues('recurringContributions');
        const frequencyValue = getValues('frequency');
        const startDateValue = getValues('recurringContributionsStartDate');
        const endDateValue = getValues('recurringContributionsEndDate');

        // Check if recurring contributions amount is greater than 0
        const isAmountValid = getIntegerValue(recurringContributionsValue) > 0;

        // Check if frequency is selected
        const isFrequencyValid = frequencyValue && frequencyValue !== undefined;

        // Check if start date is selected
        const isStartDateValid = startDateValue && startDateValue !== null;

        // Check if end date is selected
        const isEndDateValid = endDateValue && endDateValue !== null;

        return isAmountValid && isFrequencyValid && isStartDateValid && isEndDateValid;
    };

    // Custom validation function for one-time future contribution
    const isOneTimeFutureContributionValid = () => {
        if (!showOneTimeFutureContribution) {
            return true;
        }
        const oneTimeFutureAmountValue = getValues('oneTimeFutureAmount');
        const fundingYearValue = getValues('fundingYear');
        // One-time future amount validation differs based on feature flag
        const isOneTimeFutureAmountValid = getIntegerValue(oneTimeFutureAmountValue) > 0;
        // Funding year is required
        const isFundingYearValid = fundingYearValue !== undefined && fundingYearValue !== null;

        return isOneTimeFutureAmountValid && isFundingYearValid;
    };

    // Custom form validation that only considers relevant fields
    const isAmountRequirementMet = () => {
        const amountValue = getIntegerValue(getValues('amount'));
        const recurringValue = getIntegerValue(getValues('recurringContributions'));
        const oneTimeValue = getIntegerValue(getValues('oneTimeFutureAmount'));

        if (showOneTimeFutureContribution && showRecurringContributions) {
            return oneTimeValue > 0 && recurringValue > 0;
        }

        if (showOneTimeFutureContribution) {
            return oneTimeValue > 0;
        }

        if (showRecurringContributions) {
            return recurringValue > 0;
        }

        return amountValue > 0;
    };

    // Helper function to check if there are any relevant errors
    // Filters out errors from unchecked sections
    const hasRelevantErrors = () => {
        const errorEntries = Object.entries(errors);

        return errorEntries.some(([fieldName, error]) => {
            if (!error) return false;

            // Filter out recurring contributions errors when checkbox is unchecked
            if (!showRecurringContributions) {
                if (
                    fieldName === 'recurringContributions' ||
                    fieldName === 'frequency' ||
                    fieldName === 'recurringContributionsStartDate' ||
                    fieldName === 'recurringContributionsEndDate'
                ) {
                    return false;
                }
            }

            // Filter out one-time future contribution errors when checkbox is unchecked
            if (!showOneTimeFutureContribution) {
                if (fieldName === 'oneTimeFutureAmount' || fieldName === 'fundingYear') {
                    return false;
                }
            }

            return true;
        });
    };

    const isFormValid = () => {
        // New behavior with feature flag enabled
        // Check base form fields
        const baseFormValues = getValues();
        const hasBaseFields = baseFormValues.accountHolder && baseFormValues.accountType && baseFormValues.accountName;

        const amountValue = getIntegerValue(baseFormValues.amount);
        const amountRequired = !showOneTimeFutureContribution && !showRecurringContributions;
        const isAmountValid = amountRequired ? amountValue > 0 : true;

        if (!hasBaseFields || !isAmountValid) {
            return false;
        }

        if (showRecurringContributions && !isRecurringContributionsValid()) {
            return false;
        }

        if (showOneTimeFutureContribution && !isOneTimeFutureContributionValid()) {
            return false;
        }

        return true;
    };

    return (
        <DrawerComp
            header={t('ACCOUNTS_LABEL_TEXT')}
            open={openDrawer}
            handleClose={checkUnsavedAndhandleClose}
            onSubmit={submitFunds}
            drawerIcon={<img src={investmentSource} />}
            secondaryButtonLabel={t(currentMode === 'edit' ? 'SAVE_SOURCE' : 'BUTTON_ADD_ANOTHER_ACCOUNT')}
            onSecondarySubmit={handleSubmit((data) => {
                addAnotherFund(data);
            })}
            disableSubmit={isDeleteClicked() || hasRelevantErrors() || !isAmountRequirementMet() || !!infoMessage}
            hasChange={checkIfFormIsEdited()}
            isValid={!isFormValid() || hasRelevantErrors() || !!infoMessage}
            heapId="complete-accounts-drawer"
            APIError={apiErrorToken}
        >
            {accountRecommendation && (
                <StickyRegion>
                    <Box paddingBlock={2} marginTop={-2.6}>
                        <Banner type="alert" message={generateBannerMessageFromAppliedRecommendation()} />
                    </Box>
                </StickyRegion>
            )}
            {fundingAccounts?.length >= 0 && (
                <>
                    {fundingAccounts?.length > 0 ? (
                        <FundingDrawersDropdown
                            sources={fundingAccounts}
                            selectedSourceId={selectedAccountInDropdown}
                            onSourceSelect={(selectedAccountId: string) => {
                                setSelectedAccountInDropdown(selectedAccountId);
                                setNeedPrepopulation(false);
                            }}
                            onSourceEdit={(selectedAccountId: string) => {
                                if (selectedAccountId) {
                                    handleEditClick(selectedAccountId);
                                }
                            }}
                            onResetForm={handleReset}
                            onModeChange={(mode: 'add' | 'edit') => {
                                setCurrentMode(mode);
                                if (mode === 'add') {
                                    setSelectedAccountForEdit('');
                                    setAccountRecommendation(undefined);
                                }
                            }}
                            title={t('SUBTEXT_FUNDING_SOURCE_WEALTH')}
                            placeholderText={t('SELECT_ACCOUNT')}
                            dropdownTestId="saved-accounts-dropdown"
                        />
                    ) : null}
                </>
            )}
            <ConfirmationDialog
                onCancellation={handleCancelDelete}
                onCloseIconClick={handleCancelDelete}
                onConfirmation={() => handleConfirmDelete()}
                open={deleteModalState}
                content={`${t('DELETE_CONFIRMATION_MODAL_CONTENT_INVESTMENT')}`}
                confirmButtonLabel={`${t('DELETE_CONFIRMATION_MODAL_DELETE_TEXT')}`}
                cancelButtonLabel={`${t('DELETE_CONFIRMATION_MODAL_CANCEL_TEXT')}`}
                title={t('DELETE_CONFIRMATION_MODAL_HEADER_TEXT_INVESTMENT')}
                width={getResponsiveWidth()}
                cardMaxWidth="350px"
                center={true}
            />
            <Grid container marginTop={2} marginBottom={2}>
                <Typography
                    variant="headers.h4"
                    label={t(
                        currentMode === 'edit' && selectedAccountForEdit
                            ? 'DRAWER_EDIT_INVESTMENT_SOURCE'
                            : 'DRAWER_ADD_NEW_FUNDING_SOURCE'
                    )}
                />
            </Grid>
            <FundingFormWrapper key={keyId}>
                <Grid container item direction="row" xs={12} spacing={2}>
                    <Grid item xs={12}>
                        {isSingleClientOnly ? (
                            <>
                                <Typography id="source-owner-label" variant="labels.large">
                                    {`${t('SOURCE_OWNER')} *`}
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 600, color: '#000' }}>
                                    {`${adviceClient?.clientFirstName ?? ''} ${
                                        adviceClient?.clientLastName ?? ''
                                    }`.trim() || t('TEXT_CLIENT')}
                                </Typography>
                            </>
                        ) : (
                            <ItemContainer sx={{ alignItems: 'flex-start', width: '100%' }}>
                                <Controller
                                    name="accountHolder"
                                    control={control}
                                    rules={{
                                        required: { value: true, message: t('SOURCE_OWNER_IS_REQUIRED') }
                                    }}
                                    render={({ field: { onChange, value, name, onBlur } }) => (
                                        <ComboSelect
                                            onSelectChange={(val) => {
                                                setNeedPrepopulation(true);
                                                setOnEditPrepopulation(true);
                                                onChange(val);
                                                onBlur();
                                            }}
                                            onBlur={onBlur}
                                            selectedValue={value}
                                            name={`add-income-sources-${name}`}
                                            label={`${t('SOURCE_OWNER')} *`}
                                            placeholder={t('TEXT_SELECT')}
                                            options={accountHolderOptions}
                                            error={!!errors.accountHolder}
                                            size="small"
                                            fullWidth
                                            MenuProps={{
                                                PaperProps: {
                                                    sx: {
                                                        border: '1px solid #E0E0E0',
                                                        borderRadius: '8px',
                                                        '& .MuiMenuItem-root': {
                                                            color: theme.palette.neutral.black
                                                        }
                                                    }
                                                }
                                            }}
                                            renderValue={(selected) => {
                                                if (!selected)
                                                    return (
                                                        <em
                                                            style={{
                                                                color: theme.palette.info[300],
                                                                fontStyle: 'normal'
                                                            }}
                                                        >
                                                            {t('TEXT_SELECT')}
                                                        </em>
                                                    );

                                                if (selected === 'primary') {
                                                    return `${adviceClient?.clientFirstName ?? ''} ${
                                                        adviceClient?.clientLastName ?? ''
                                                    }`.trim();
                                                }
                                                if (selected === 'joint') {
                                                    return t('TEXT_JOINT');
                                                }
                                                const relationship = adviceClient?.relationship?.find(
                                                    (r) => r.id === selected
                                                );
                                                if (relationship) {
                                                    return `${relationship.firstName ?? ''} ${
                                                        relationship.lastName ?? ''
                                                    }`.trim();
                                                }
                                                return selected;
                                            }}
                                        />
                                    )}
                                />
                                {errors.accountHolder ? (
                                    <ErrorSpan variant="subtitle2" role="alert" label={errors.accountHolder.message} />
                                ) : null}
                            </ItemContainer>
                        )}
                    </Grid>
                </Grid>
                <ItemContainer sx={{ alignItems: 'flex-start' }}>
                    <Typography variant="labels.large" label={`${t('TEXT_ACCOUNT_TYPE')} *`} />
                    <Controller
                        name="accountType"
                        control={control}
                        rules={{
                            required: {
                                value: true,
                                message: t('TEXT_THIS_IS_REQUIRED_FIELD')
                            }
                        }}
                        render={({ field }) => (
                            <ComboSelect
                                {...field}
                                size="small"
                                onSelectChange={(val) => {
                                    setNeedPrepopulation(true);
                                    setOnEditPrepopulation(true);
                                    field.onChange(val);
                                    field.onBlur();
                                }}
                                selectedValue={field.value}
                                options={
                                    orgInfo?.investmentAccountType?.[language] ||
                                    orgInfo?.investmentAccountType?.['en'] ||
                                    []
                                }
                                data-testid="accountType"
                                error={!!errors.accountType}
                                listItemStyle={{
                                    padding: '0px 14px'
                                }}
                                renderMenuItem={(option: { label: string; value: string; duration?: string }) => (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                                        {option.duration && (
                                            <Chip
                                                label={t(option.duration.split(' ')[0].toUpperCase())}
                                                size="small"
                                                sx={{
                                                    backgroundColor: getDurationColor(theme, option.duration),
                                                    minWidth: '100px',
                                                    borderRadius: '16px'
                                                }}
                                            />
                                        )}
                                        <Box component="span">{option.label}</Box>
                                    </Box>
                                )}
                                renderValue={(selected) => {
                                    if (!selected)
                                        return (
                                            <em
                                                style={{
                                                    color: theme.palette.info[400],
                                                    fontStyle: 'normal'
                                                }}
                                            >
                                                {t('TEXT_SELECT')}
                                            </em>
                                        );
                                    const selectedOption = (orgInfo?.investmentAccountType?.[language] || []).find(
                                        (option: { value: any }) => option.value === selected
                                    );
                                    if (selectedOption) {
                                        return (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                                                {selectedOption.duration && (
                                                    <Chip
                                                        label={t(selectedOption.duration.split(' ')[0].toUpperCase())}
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: getDurationColor(
                                                                theme,
                                                                selectedOption.duration
                                                            ),
                                                            minWidth: '100px',
                                                            borderRadius: '16px'
                                                        }}
                                                    />
                                                )}
                                                <Box component="span">{selectedOption.label}</Box>
                                            </Box>
                                        );
                                    }
                                }}
                            />
                        )}
                    />
                    {errors.accountType ? (
                        <ErrorSpan variant="subtitle2" role="alert" label={errors.accountType.message} />
                    ) : null}
                </ItemContainer>
                <ItemContainer sx={{ alignItems: 'flex-start', width: '100%' }}>
                    <Typography variant="labels.large" label={`${t('TEXT_ACCOUNT_NAME')} *`} />
                    <Controller
                        name="accountName"
                        control={control}
                        rules={{
                            required: {
                                value: true,
                                message: t('TEXT_ACCOUNT_NAME_REQUIRED_FIELD')
                            },
                            pattern: {
                                value: /^(?!\s)(?!.*\s$)([A-Za-z0-9\s’'.\-&/+()áéíóúüñÁÉÍÓÚÜÑàâäèêëîïôöùûçÀÂÄÈÊËÎÏÔÖÙÛÇ])*$/i,
                                message: t('TEXT_ACCOUNT_NAME_VALIDATION')
                            },
                            maxLength: {
                                value: 50,
                                message: t('TEXT_ACCOUNT_NAME_MAX_LENGTH')
                            }
                        }}
                        render={({ field: { onChange, value, ref } }) => (
                            <TextboxInput
                                onChange={onChange}
                                value={value}
                                inputRef={ref}
                                size="small"
                                error={!!errors.accountName}
                                type="text"
                                data-testid="account-name"
                                fullWidth
                                min={0}
                            />
                        )}
                    />
                    {errors.accountName ? (
                        <ErrorSpan variant="subtitle2" role="alert" label={errors.accountName.message} />
                    ) : null}
                </ItemContainer>

                <Grid container item direction="row" xs={12} spacing={1.5}>
                    <Grid item xs={12} sx={{ display: 'flex', gap: '8px', flexDirection: 'column' }}>
                        <ItemContainer sx={{ alignItems: 'flex-start', width: '100%' }}>
                            <Typography variant="labels.large" label={t('CURENT_BALANCE')} />
                            <Controller
                                name="amount"
                                control={control}
                                rules={{
                                    validate: (value) => {
                                        const numericValue = getIntegerValue(value);

                                        if (!showOneTimeFutureContribution && !showRecurringContributions) {
                                            return numericValue > 0 || t('TEXT_THIS_IS_REQUIRED_FIELD');
                                        }

                                        if (value === undefined || value === null || String(value).trim() === '') {
                                            return true;
                                        }

                                        return numericValue >= 0 || t('TEXT_THIS_IS_REQUIRED_FIELD');
                                    }
                                }}
                                render={({ field: { value, ref } }) => (
                                    <TextboxInput
                                        onChange={(e) => {
                                            handleAmountChange(e.target.value);
                                        }}
                                        value={
                                            value === undefined || value?.toString() === ''
                                                ? ''
                                                : formatCurrency(value, true)
                                        }
                                        inputRef={ref}
                                        size="small"
                                        min={0}
                                        error={!!errors.amount}
                                        type="text"
                                        data-testid="Amount"
                                        placeholder={currencySymbol || DEFAULT_CURRENCY_SYMBOL}
                                        fullWidth
                                    />
                                )}
                            />
                            {errors.amount ? (
                                <ErrorSpan variant="subtitle2" role="alert" label={errors.amount.message} />
                            ) : null}
                            {Boolean(accountRecommendation?.oneTimeTopUp) && (
                                <Grid display="flex" alignItems="baseline" gap={0.5}>
                                    <StyledRecommendationHelperText
                                        label={t('RECOMMENDATION_LUMPSUM_HELPER_MESSAGE', {
                                            lumpsumHelperAmount: formatCurrency(
                                                Number(accountRecommendation?.oneTimeTopUp) +
                                                    Number(
                                                        accountRecommendation?.originalData?.formFields?.sources
                                                            ?.investmentSource[0]?.accountBalance || 0
                                                    )
                                            )
                                        })}
                                    />
                                    <InfoTooltip
                                        placement="bottom"
                                        width={312}
                                        message={t('RECOMMENDATION_LUMPSUM_INFO_TOOLTIP')}
                                    />
                                </Grid>
                            )}
                        </ItemContainer>
                    </Grid>
                </Grid>
                {/* One Time Future Contribution Section */}
                <Grid container item direction="row" xs={12} alignItems="center" spacing={1} marginBottom={0}>
                    <Grid item>
                        <Box
                            onClick={() => {
                                const newValue = !showOneTimeFutureContribution;
                                setShowOneTimeFutureContribution(newValue);
                            }}
                            sx={{
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center'
                            }}
                        >
                            <Checkbox isSelected={showOneTimeFutureContribution} />
                        </Box>
                    </Grid>
                    <Grid item>
                        <Typography
                            variant="labels.large"
                            label={t('ONE_TIME_FUTURE_CONTRIBUTION_CHECKBOX_LABEL')}
                            sx={{
                                cursor: 'pointer'
                            }}
                            onClick={() => {
                                const newValue = !showOneTimeFutureContribution;
                                setShowOneTimeFutureContribution(newValue);
                            }}
                        />
                    </Grid>
                </Grid>
                {showOneTimeFutureContribution && (
                    <>
                        <Grid container item direction="row" xs={12} spacing={2}>
                            <Grid item xs={6} sx={{ display: 'flex', gap: '8px', flexDirection: 'column' }}>
                                <Typography variant="labels.large" label={t('ONE_TIME_FUTURE_CONTRIBUTION')} />
                                <Controller
                                    name="oneTimeFutureAmount"
                                    control={control}
                                    rules={{
                                        validate: (value) => {
                                            if (showOneTimeFutureContribution) {
                                                // When checkbox is enabled, field is mandatory
                                                if (
                                                    value === undefined ||
                                                    value === null ||
                                                    String(value).trim() === ''
                                                ) {
                                                    return t('TEXT_THIS_IS_REQUIRED_FIELD');
                                                }
                                                const numericValue = getIntegerValue(value);
                                                // Must be a positive integer when checkbox is enabled
                                                if (isNaN(numericValue) || numericValue <= 0) {
                                                    return t('TEXT_THIS_IS_REQUIRED_FIELD');
                                                }

                                                return true;
                                            }
                                            return true;
                                        }
                                    }}
                                    render={({ field: { value, ref } }) => (
                                        <TextboxInput
                                            onChange={(e) => {
                                                handleOneTimeFutureAmountChange(e.target.value);
                                            }}
                                            size="small"
                                            value={
                                                value === undefined || value?.toString() === ''
                                                    ? ''
                                                    : formatCurrency(+value, true)
                                            }
                                            inputRef={ref}
                                            min={0}
                                            error={!!errors.oneTimeFutureAmount}
                                            type="text"
                                            data-testid="one-time-future-amount"
                                            placeholder={currencySymbol || DEFAULT_CURRENCY_SYMBOL}
                                            fullWidth
                                        />
                                    )}
                                />
                                {errors.oneTimeFutureAmount ? (
                                    <ErrorSpan
                                        variant="subtitle2"
                                        role="alert"
                                        label={errors.oneTimeFutureAmount.message}
                                    />
                                ) : null}
                            </Grid>
                            <Grid item xs={6}>
                                <ItemContainer sx={{ alignItems: 'flex-start' }}>
                                    <Typography variant="labels.large" label={t('ONE_TIME_FUTURE_CONTRIBUTION_YEAR')} />
                                    <Controller
                                        name="fundingYear"
                                        control={control}
                                        rules={{
                                            validate: (value) => {
                                                if (showOneTimeFutureContribution) {
                                                    if (!value) return t('TEXT_THIS_IS_REQUIRED_FIELD');
                                                    const date = dayjs(value);
                                                    if (!date.isValid()) return t('TEXT_VALID_FUTURE_YEAR_ERROR');
                                                    const now = dayjs();
                                                    if (isMonthYearDatesEnabled()) {
                                                        // Disable current and past months
                                                        if (!date.isAfter(now, 'month')) {
                                                            return t('TEXT_VALID_FUTURE_YEAR_ERROR');
                                                        }
                                                    } else if (date.year() <= now.year()) {
                                                        // Disable current year and past years
                                                        return t('TEXT_VALID_FUTURE_YEAR_ERROR');
                                                    }
                                                    return true;
                                                }
                                                return true;
                                            }
                                        }}
                                        render={({ field: { onChange, onBlur, value } }) => (
                                            <BasicDatePicker
                                                key={`fundingYear-${keyId}`}
                                                enabled={!!showOneTimeFutureContribution}
                                                type="single"
                                                startDate={
                                                    isMonthYearDatesEnabled()
                                                        ? dayjs().add(1, 'month').startOf('month').toString()
                                                        : dayjs().add(1, 'year').startOf('year').toString()
                                                }
                                                {...(isMonthYearDatesEnabled() ? { isMmYyyy: true } : { isYyyy: true })}
                                                value={value ? dayjs(value) : null}
                                                onChange={(event: any) => {
                                                    if (!event || event === '' || event === null) {
                                                        onChange(undefined);
                                                    } else {
                                                        handleDateChange(event, onChange, 'fundingYear');
                                                    }
                                                    onBlur();
                                                }}
                                                onBlur={onBlur}
                                                data-testid="one-time-future-contribution-year"
                                                hasError={!!errors.fundingYear}
                                            />
                                        )}
                                    />
                                    {errors.fundingYear ? (
                                        <ErrorSpan role="alert">{errors.fundingYear.message}</ErrorSpan>
                                    ) : null}
                                </ItemContainer>
                            </Grid>
                        </Grid>
                    </>
                )}
                <>
                    <Grid container item direction="row" xs={12} alignItems="center" spacing={1} marginBottom={2}>
                        <Grid item>
                            <Box
                                onClick={() => {
                                    const newValue = !showRecurringContributions;
                                    setShowRecurringContributions(newValue);
                                }}
                                sx={{
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center'
                                }}
                            >
                                <Checkbox isSelected={showRecurringContributions} />
                            </Box>
                        </Grid>
                        <Grid item>
                            <Typography
                                variant="labels.large"
                                label={`${t('RECURRING_CONTRIBUTIONS_CHECKBOX_LABEL')}`}
                                sx={{
                                    cursor: 'pointer'
                                }}
                                onClick={() => {
                                    const newValue = !showRecurringContributions;
                                    setShowRecurringContributions(newValue);
                                }}
                            />
                        </Grid>
                    </Grid>
                </>
                {showRecurringContributions && (
                    <>
                        {infoMessage && (
                            <InfoMessage>
                                <InfoErrorIcon />
                                <span>{infoMessage}</span>
                            </InfoMessage>
                        )}
                        <Grid container item direction="row" xs={12} spacing={2}>
                            <Grid item xs={6} sx={{ display: 'flex', gap: '8px', flexDirection: 'column' }}>
                                <Typography variant="labels.large" label={`${t('TEXT_RECURRING_CONTRIBUTIONS')}`} />
                                <Controller
                                    name="recurringContributions"
                                    control={control}
                                    rules={{
                                        validate: (value) => {
                                            if (showRecurringContributions) {
                                                if (!value || getIntegerValue(value) <= 0) {
                                                    return t('TEXT_THIS_IS_REQUIRED_FIELD');
                                                }
                                                return getIntegerValue(value) >= 0 || t('TEXT_MIN_VALUE');
                                            }
                                            if (value) {
                                                return getIntegerValue(value) >= 0 || t('TEXT_MIN_VALUE');
                                            }
                                            return true;
                                        }
                                    }}
                                    render={({ field: { value, ref } }) => (
                                        <TextboxInput
                                            onChange={(e) => {
                                                handleRecurringContributionsChange(e.target.value);
                                            }}
                                            size="small"
                                            value={
                                                value === undefined || value?.toString() === ''
                                                    ? ''
                                                    : formatCurrency(value, true)
                                            }
                                            inputRef={ref}
                                            min={undefined}
                                            error={!!errors.recurringContributions}
                                            type="text"
                                            data-testid="recurring-contributions"
                                            placeholder={currencySymbol || DEFAULT_CURRENCY_SYMBOL}
                                            fullWidth
                                        />
                                    )}
                                />
                                {errors.recurringContributions ? (
                                    <ErrorSpan
                                        variant="subtitle2"
                                        role="alert"
                                        label={errors.recurringContributions.message}
                                    />
                                ) : null}
                            </Grid>
                            <Grid item xs={6}>
                                <ItemContainer sx={{ alignItems: 'flex-start' }}>
                                    <Typography variant="labels.large" label={`${t('FREQUENCY')}`} />
                                    <Controller
                                        name="frequency"
                                        control={control}
                                        rules={{
                                            validate: (value) => {
                                                if (showRecurringContributions) {
                                                    return !!value || t('TEXT_THIS_IS_REQUIRED_FIELD');
                                                }
                                                if (
                                                    recurringContributions &&
                                                    Number(removeNonNumeric(recurringContributions)) > 0
                                                ) {
                                                    return (
                                                        (Number(removeNonNumeric(recurringContributions)) > 0 &&
                                                            !!value) ||
                                                        t('TEXT_THIS_IS_REQUIRED_FIELD')
                                                    );
                                                }
                                                return true;
                                            }
                                        }}
                                        render={({ field }) => (
                                            <ComboSelect
                                                {...field}
                                                size="small"
                                                onSelectChange={async (val: string) => {
                                                    setValue('frequency', val, { shouldValidate: true });
                                                    if (recurringContributionsEndDateValue) {
                                                        await trigger('recurringContributionsEndDate');
                                                        await trigger('recurringContributionsStartDate');
                                                    }
                                                }}
                                                selectedValue={field.value}
                                                options={[
                                                    { label: t('ANNUALLY'), value: 'Annually' },
                                                    { label: t('MONTHLY'), value: 'Monthly' }
                                                ]}
                                                data-testid="frequency"
                                                error={!!errors.frequency}
                                            />
                                        )}
                                    />
                                    {errors.frequency ? (
                                        <ErrorSpan variant="subtitle2" role="alert" label={errors.frequency.message} />
                                    ) : null}
                                </ItemContainer>
                            </Grid>
                        </Grid>
                        <div className="go-horizontal-align-drawer">
                            <Grid container item xs={6} direction="column">
                                <Controller
                                    name="recurringContributionsStartDate"
                                    control={control}
                                    rules={{
                                        validate: (value) => {
                                            if (showRecurringContributions) {
                                                if (!value) return !!value;

                                                const date = dayjs(value, 'DD-MM-YYYY');
                                                if (!date.isValid()) return t('TEXT_VALID_DATE_ERROR');

                                                const now = dayjs();

                                                if (
                                                    !isMonthYearDatesEnabled() &&
                                                    getValues('frequency') === 'Annually'
                                                ) {
                                                    if (date.year() <= now.year()) {
                                                        return t('TEXT_VALID_DATE_ERROR');
                                                    }
                                                } else if (date.isBefore(now, 'month')) {
                                                    return t('TEXT_VALID_DATE_ERROR');
                                                }
                                                return true;
                                            }
                                            if (
                                                recurringContributions &&
                                                Number(removeNonNumeric(recurringContributions)) > 0
                                            ) {
                                                if (!value) return;

                                                const date = dayjs(value, 'DD-MM-YYYY');
                                                if (!date.isValid()) return t('TEXT_VALID_DATE_ERROR');

                                                const now = dayjs();

                                                if (
                                                    !isMonthYearDatesEnabled() &&
                                                    getValues('frequency') === 'Annually'
                                                ) {
                                                    if (date.year() <= now.year()) {
                                                        return t('TEXT_VALID_DATE_ERROR');
                                                    }
                                                } else if (date.isBefore(now, 'month')) {
                                                    return t('TEXT_VALID_DATE_ERROR');
                                                }
                                                return true;
                                            }
                                        }
                                    }}
                                    render={({ field }) => (
                                        <>
                                            <Container>
                                                <Typography
                                                    variant="labels.large"
                                                    sx={{ color: theme.palette.neutral[300] }}
                                                    label={`${t('STARTING_AT_LABEL')}*`}
                                                />
                                                <InfoTooltip message={t('RECURRING_STARTING_INFO_TEXT')} />
                                            </Container>
                                            <RadioWithOptionalDatePicker
                                                enabled={!!frequency}
                                                field={{
                                                    ...field,
                                                    value: field.value
                                                        ? typeof field.value === 'string'
                                                            ? field.value
                                                            : dayjs(field.value).format('DD-MM-YYYY')
                                                        : field.value,
                                                    onChange: (arg: string) => {
                                                        field.onChange(arg);
                                                        trigger('recurringContributionsEndDate');
                                                    }
                                                }}
                                                selectedKey={recurringContributionsStartDateKey}
                                                onSelectedKeyChange={setRecurringContributionsStartDateKey}
                                                key={`start-date-picker-${frequency || 'no-frequency'}`}
                                                isMMYYYY={isMonthYearDatesEnabled()}
                                                isYYYY={!isMonthYearDatesEnabled() && frequency === 'Annually'}
                                                options={[
                                                    {
                                                        label: (
                                                            <>
                                                                <Typography
                                                                    variant="labels.largeDemibold"
                                                                    label={
                                                                        !isMonthYearDatesEnabled() &&
                                                                        frequency === 'Annually'
                                                                            ? t('NEXT_YEAR')
                                                                            : t('NEXT_MONTH')
                                                                    }
                                                                />
                                                                <Typography
                                                                    variant="labels.large"
                                                                    label={
                                                                        !isMonthYearDatesEnabled() &&
                                                                        frequency === 'Annually'
                                                                            ? `${dayjs()
                                                                                  .add(1, 'year')
                                                                                  .startOf('year')
                                                                                  .format('MMMM YYYY')}, ${t(
                                                                                  'AGE_LABEL'
                                                                              )} ${
                                                                                  Math.floor(
                                                                                      dayjs().diff(
                                                                                          dayjs(
                                                                                              adviceClient?.clientDob,
                                                                                              'DD-MM-YYYY'
                                                                                          ),
                                                                                          'years',
                                                                                          true
                                                                                      )
                                                                                  ) + 1
                                                                              }`
                                                                            : `${dayjs()
                                                                                  .add(1, 'month')
                                                                                  .format('MMMM YYYY')}, ${t(
                                                                                  'AGE_LABEL'
                                                                              )} ${Math.floor(
                                                                                  dayjs().diff(
                                                                                      dayjs(
                                                                                          adviceClient?.clientDob,
                                                                                          'DD-MM-YYYY'
                                                                                      ),
                                                                                      'years',
                                                                                      true
                                                                                  )
                                                                              )}`
                                                                    }
                                                                />
                                                            </>
                                                        ),
                                                        value:
                                                            !isMonthYearDatesEnabled() && frequency === 'Annually'
                                                                ? dayjs().add(1, 'year').format('DD-MM-YYYY')
                                                                : dayjs()
                                                                      .add(1, 'month')
                                                                      .startOf('month')
                                                                      .format('DD-MM-YYYY'),
                                                        disabled: !frequency
                                                    }
                                                ].filter(Boolean)}
                                            />
                                        </>
                                    )}
                                />
                                {errors.recurringContributionsStartDate && (
                                    <ErrorSpan
                                        variant="subtitle2"
                                        role="alert"
                                        label={errors?.recurringContributionsStartDate?.message}
                                    />
                                )}
                            </Grid>
                            <Grid container item xs={6} direction="column">
                                <Controller
                                    name="recurringContributionsEndDate"
                                    control={control}
                                    rules={{
                                        validate: (value) => {
                                            if (showRecurringContributions) {
                                                if (!value) return !!value;

                                                const endDate = dayjs(value, 'DD-MM-YYYY');
                                                if (!endDate.isValid()) return t('TEXT_VALID_END_DATE_ERROR');

                                                const rawStartDate = getValues('recurringContributionsStartDate');
                                                const startDate = dayjs(rawStartDate, 'DD-MM-YYYY');
                                                if (!startDate.isValid()) return;

                                                if (isMonthYearDatesEnabled()) {
                                                    if (!endDate.isAfter(startDate, 'month')) {
                                                        return t('TEXT_VALID_END_DATE_ERROR');
                                                    }
                                                } else if (getValues('frequency') === 'Annually') {
                                                    const startYear = startDate.year();
                                                    const endYear = endDate.year();

                                                    if (endYear === startYear) {
                                                        return t('TEXT_START_END_YEAR_SHOULD_NOT_BE_SAME');
                                                    }

                                                    if (endYear < startYear) {
                                                        return t('TEXT_VALID_END_DATE_ERROR');
                                                    }
                                                } else if (!endDate.isAfter(startDate)) {
                                                    return t('TEXT_VALID_END_DATE_ERROR');
                                                }
                                                return true;
                                            }
                                            if (
                                                recurringContributions &&
                                                Number(removeNonNumeric(recurringContributions)) > 0
                                            ) {
                                                if (!value) return;

                                                const endDate = dayjs(value, 'DD-MM-YYYY');
                                                if (!endDate.isValid()) return t('TEXT_VALID_END_DATE_ERROR');

                                                const rawStartDate = getValues('recurringContributionsStartDate');
                                                const startDate = dayjs(rawStartDate, 'DD-MM-YYYY');
                                                if (!startDate.isValid()) return t('TEXT_THIS_IS_REQUIRED_FIELD');

                                                if (isMonthYearDatesEnabled()) {
                                                    if (!endDate.isAfter(startDate, 'month')) {
                                                        return t('TEXT_VALID_END_DATE_ERROR');
                                                    }
                                                } else if (getValues('frequency') === 'Annually') {
                                                    const startYear = startDate.year();
                                                    const endYear = endDate.year();

                                                    if (endYear === startYear) {
                                                        return t('TEXT_START_END_YEAR_SHOULD_NOT_BE_SAME');
                                                    }

                                                    if (endYear < startYear) {
                                                        return t('TEXT_VALID_END_DATE_ERROR');
                                                    }
                                                } else if (!endDate.isAfter(startDate, 'month')) {
                                                    return t('TEXT_VALID_END_DATE_ERROR');
                                                }
                                                return true;
                                            }
                                        }
                                    }}
                                    render={({ field }) => (
                                        <>
                                            <Typography variant="labels.large" label={`${t('ENDING_AT_LABEL')} *`} />
                                            <RadioWithOptionalDatePicker
                                                enabled={!!frequency}
                                                field={{
                                                    ...field,
                                                    onChange: (arg: string) => {
                                                        field.onChange(arg);
                                                        trigger('recurringContributionsStartDate');
                                                        field.onBlur();
                                                    }
                                                }}
                                                selectedKey={recurringContributionsEndDateKey}
                                                onSelectedKeyChange={setRecurringContributionsEndDateKey}
                                                key={`end-date-picker-${frequency || 'no-frequency'}`}
                                                isMMYYYY={isMonthYearDatesEnabled()}
                                                isYYYY={!isMonthYearDatesEnabled() && frequency === 'Annually'}
                                                options={[
                                                    {
                                                        label: (
                                                            <>
                                                                <Typography
                                                                    variant="labels.largeDemibold"
                                                                    label={`${
                                                                        adviceClient?.clientFirstName || t('CLIENT')
                                                                    }'s ${t('RETIREMENT_DATE_TEXT')}`}
                                                                />
                                                                <Typography
                                                                    variant="labels.large"
                                                                    label={`${addAgeToDate(
                                                                        adviceClient?.clientDob,
                                                                        adviceClient?.retirementAge ||
                                                                            DEFAULT_RETIREMENT_AGE,
                                                                        'MMMM YYYY'
                                                                    )}, ${t('AGE_LABEL')} ${
                                                                        adviceClient?.retirementAge ||
                                                                        DEFAULT_RETIREMENT_AGE
                                                                    }`}
                                                                />
                                                            </>
                                                        ),
                                                        value: addAgeToDate(
                                                            adviceClient?.clientDob,
                                                            adviceClient?.retirementAge || DEFAULT_RETIREMENT_AGE
                                                        ),
                                                        disabled: !frequency
                                                    },
                                                    adviceClient?.relationship?.[0]?.retirementAge && {
                                                        label: (
                                                            <>
                                                                <Typography
                                                                    variant="labels.largeDemibold"
                                                                    label={`${
                                                                        adviceClient?.relationship?.[0]?.firstName ||
                                                                        t('CO_CLIENT_LABEL')
                                                                    }'s ${t('RETIREMENT_DATE_TEXT')}`}
                                                                />
                                                                <Typography
                                                                    variant="labels.large"
                                                                    label={`${addAgeToDate(
                                                                        adviceClient?.relationship?.[0]?.age,
                                                                        adviceClient?.relationship?.[0]
                                                                            ?.retirementAge || DEFAULT_RETIREMENT_AGE,
                                                                        'MMMM YYYY'
                                                                    )}, ${t('AGE_LABEL')} ${
                                                                        adviceClient?.relationship?.[0]
                                                                            ?.retirementAge || DEFAULT_RETIREMENT_AGE
                                                                    }`}
                                                                />
                                                            </>
                                                        ),
                                                        value: addAgeToDate(
                                                            adviceClient?.relationship?.[0]?.age,
                                                            adviceClient?.relationship?.[0]?.retirementAge ||
                                                                DEFAULT_RETIREMENT_AGE
                                                        ),
                                                        disabled: !frequency
                                                    }
                                                ].filter(Boolean)}
                                            />
                                        </>
                                    )}
                                />
                                {errors.recurringContributionsEndDate && (
                                    <ErrorSpan role="alert">{errors.recurringContributionsEndDate.message}</ErrorSpan>
                                )}
                            </Grid>
                        </div>
                    </>
                )}
            </FundingFormWrapper>

            {/* Delete Account button when in edit mode with feature flag enabled */}
            {(selectedAccountInDropdown || selectedAccountForEdit) && currentMode === 'edit' && (
                <DeleteContainer>
                    <BasicButton
                        variant="text"
                        textVariantStyle="capitalize"
                        color="error"
                        size="medium"
                        onClick={() => handleDeleteClick(selectedAccountInDropdown || selectedAccountForEdit)}
                        data-testid="delete-account-button"
                    >
                        {t('DELETE_ACCOUNT')}
                    </BasicButton>
                </DeleteContainer>
            )}
        </DrawerComp>
    );
};
